import PropTypes from 'prop-types'
import { sliceStatusPropTypes } from '../slice.types'

/**
 * @typedef {'unused' | 'authorizing' | 'authorized' | 'captured' | 'failed'} PaymentStatus
 */

/**
 * @typedef {Object} MultiAddressSelectionItem
 * @property {string} cartItemId - Unique cart item identifier
 * @property {Object} address - Customer address object
 * @property {number} address.id - Customer address ID
 * @property {string} address.full_name - Full name
 * @property {string} address.address_line_1 - Address line 1
 * @property {Object} [quantity] - Quantity object
 * @property {number} quantity.value - Quantity to ship to this address
 */

/**
 * @typedef {Object} GuestCheckoutData
 * @property {string} [contact_email] - Guest's contact email
 * @property {Object} [selected_customer_address] - Selected shipping address (guest only)
 * @property {Object[]} [guest_addresses] - List of addresses entered by guest
 * @property {boolean} [use_shipping_as_billing] - Whether to use shipping address as billing
 */

/**
 * @typedef {Object} CheckoutSessionData
 * @property {MultiAddressSelectionItem[]} [multi_address_selection] - Multi-address selections
 * @property {Object} [selected_billing_address] - Selected billing address (all users)
 * @property {GuestCheckoutData} [guest_checkout_data] - Guest checkout specific data
 */

/**
 * @typedef {Object} CheckoutSessionTokenState
 * @property {string|null} token - Full token for API calls
 * @property {string|null} shortCode - Display code (e.g., "A3B7K9")
 * @property {MultiAddressSelectionItem[]} multiAddressSelection - Persisted selections from backend
 * @property {Object|null} selectedBillingAddress - Selected billing address (all users)
 * @property {number|null} selectedCustomerAddressId - Selected shipping address ID (logged-in single-address checkout)
 * @property {GuestCheckoutData|null} guestCheckoutData - Guest checkout data from backend
 * @property {PaymentStatus|null} paymentStatus - Payment status
 * @property {boolean} used - Whether token has been consumed
 */

/** @typedef {import('../slice.types').BaseSlice & CheckoutSessionTokenState} CheckoutSessionTokenSlice */

/** @type {CheckoutSessionTokenSlice} */
export const initialCheckoutSessionTokenState = {
  status: 'init',
  errors: [],
  token: null,
  shortCode: null,
  multiAddressSelection: [],
  selectedBillingAddress: null,
  selectedCustomerAddressId: null,
  guestCheckoutData: null,
  paymentStatus: null,
  used: false
}

/** @enum {string} */
export const CHECKOUT_SESSION_TOKEN_ACTIONS = {
  SET_STATUS: 'CHECKOUT_SESSION_TOKEN/SET_STATUS',
  SET_ERRORS: 'CHECKOUT_SESSION_TOKEN/SET_ERRORS',
  SET_TOKEN: 'CHECKOUT_SESSION_TOKEN/SET_TOKEN',
  SET_SHORT_CODE: 'CHECKOUT_SESSION_TOKEN/SET_SHORT_CODE',
  SET_MULTI_ADDRESS_SELECTION: 'CHECKOUT_SESSION_TOKEN/SET_MULTI_ADDRESS_SELECTION',
  SET_SELECTED_BILLING_ADDRESS: 'CHECKOUT_SESSION_TOKEN/SET_SELECTED_BILLING_ADDRESS',
  SET_SELECTED_CUSTOMER_ADDRESS_ID: 'CHECKOUT_SESSION_TOKEN/SET_SELECTED_CUSTOMER_ADDRESS_ID',
  SET_GUEST_CHECKOUT_DATA: 'CHECKOUT_SESSION_TOKEN/SET_GUEST_CHECKOUT_DATA',
  SET_PAYMENT_STATUS: 'CHECKOUT_SESSION_TOKEN/SET_PAYMENT_STATUS',
  SET_USED: 'CHECKOUT_SESSION_TOKEN/SET_USED',
  SET_TOKEN_DATA: 'CHECKOUT_SESSION_TOKEN/SET_TOKEN_DATA',
  CLEAR_ERRORS: 'CHECKOUT_SESSION_TOKEN/CLEAR_ERRORS',
  CLEAR_TOKEN: 'CHECKOUT_SESSION_TOKEN/CLEAR_TOKEN'
}

export const multiAddressSelectionItemPropType = PropTypes.shape({
  cartItemId: PropTypes.string.isRequired,
  address: PropTypes.shape({
    id: PropTypes.number.isRequired,
    full_name: PropTypes.string,
    address_line_1: PropTypes.string
  }).isRequired,
  quantity: PropTypes.shape({
    value: PropTypes.number
  })
})

export const guestCheckoutDataPropType = PropTypes.shape({
  contact_email: PropTypes.string,
  selected_customer_address: PropTypes.object,
  guest_addresses: PropTypes.arrayOf(PropTypes.object),
  use_shipping_as_billing: PropTypes.bool
})

export const checkoutSessionTokenSlicePropType = PropTypes.shape({
  status: sliceStatusPropTypes,
  errors: PropTypes.arrayOf(PropTypes.shape({ message: PropTypes.string })),
  token: PropTypes.string,
  shortCode: PropTypes.string,
  multiAddressSelection: PropTypes.arrayOf(multiAddressSelectionItemPropType),
  selectedBillingAddress: PropTypes.object,
  selectedCustomerAddressId: PropTypes.number,
  guestCheckoutData: guestCheckoutDataPropType,
  paymentStatus: PropTypes.oneOf(['unused', 'authorizing', 'authorized', 'captured', 'failed']),
  used: PropTypes.bool
})

/**
 * Action type definitions
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: CheckoutSessionTokenSlice['status'] }} SetStatusAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: import('../slice.types').SliceError }} SetErrorsAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: string }} SetTokenAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: string }} SetShortCodeAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: MultiAddressSelectionItem[] }} SetMultiAddressSelectionAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: number }} SetSelectedCustomerAddressIdAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: PaymentStatus }} SetPaymentStatusAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: boolean }} SetUsedAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS, payload: Partial<CheckoutSessionTokenState> }} SetTokenDataAction
 * @typedef {{ type: CHECKOUT_SESSION_TOKEN_ACTIONS }} ClearAction
 *
 * @typedef {SetStatusAction | SetErrorsAction | SetTokenAction | SetShortCodeAction | SetMultiAddressSelectionAction | SetSelectedCustomerAddressIdAction | SetPaymentStatusAction | SetUsedAction | SetTokenDataAction | ClearAction} CheckoutSessionTokenAction
 */
